// 위젯 단위 역전파(write-back) 가용 판정 + 안내문 — 순수 함수(EditorPage `WriteBackSelector`가 읽는다).
// 판정(`writeBackAvailability`)과 select 아래 안내문(`writeBackHelpText`)이 여기 산다 —
// co-located spec이 문장을 못박는다(은닉 감사가 통과한 문구 — 글자 그대로 보존, 여기서 바꾸지 않는다).

import type { CatalogEntry } from '@safetyhub/shared';
import {
  ROW_SCOPE_WRITE_BACK_ELIGIBLE_NOTE,
  type RowScopeWriteBackJudgement,
} from './rowScopeWriteBack';

/**
 * 역전파(write-back) 가능 여부·저장 대상 판정 — prefillFrom + 카탈로그 writer 등록으로 자동 결정(순수 함수).
 * - prefill 없음 → 제네릭 저장소(현장/조직 모두 선택 가능)
 * - prefill + 카탈로그 writer(writableFields) 필드 매칭 → 도메인 정본(entry.writeBack.scope 스코프 —
 *   미선언이면 종전 규약=현장 전용)
 * - prefill + writer 미등록 / 필드 비매칭 → 비활성(사유)
 * 결정: docs/decisions/2026-07-13-composer-catalog-writeback.md
 */
export type WriteBackAvailability =
  | { kind: 'generic' }
  | {
      kind: 'catalog';
      catalogLabel: string;
      scope?: 'site' | 'organization';
      /** 표시값(콤보 textField 등)이라 정본 필드 저장은 안 되지만 선택 기억(useAsDefault)은 유효한 경우. */
      rememberOnly?: boolean;
    }
  | { kind: 'disabled'; reason: string };

/**
 * select 경로가 writableFields 중 하나로 시작(prefix)하는지 — 선두 숫자 인덱스(행) 세그먼트는 제거 후 판정.
 * 조건 세그먼트(`categories[key=desk]`)는 base 필드(`categories`)로 환원 후 판정한다
 * (enum valueSet 전개 select `0.categories[key=desk].count`도 writable 인식).
 * 서버 대응: apps/server/src/report-composer/report-composer.writeback.ts isWritablePath/stripCondSegment(동기화 필요).
 */
export function writeBackWritableFieldMatch(
  select: string | undefined,
  writableFields: string[],
): boolean {
  if (!select) return false;
  const segs = select.split('.').filter((s) => s !== '');
  if (segs.length > 0 && /^\d+$/.test(segs[0])) segs.shift();
  if (segs.length === 0) return false; // 인덱스만 있고 필드 경로가 없으면 비매칭
  const rest = segs
    .map((seg) => {
      const m = seg.match(/^([^[\]]*)\[([^=[\]]+)=([^[\]]*)\]$/);
      return m ? m[1] : seg;
    })
    .join('.');
  return writableFields.some((wf) => rest === wf || rest.startsWith(`${wf}.`));
}

export function writeBackAvailability(
  prefillFrom: { catalogKey: string; select?: string } | undefined,
  catalog: CatalogEntry[] | undefined,
): WriteBackAvailability {
  if (!prefillFrom) return { kind: 'generic' };
  const entry = (catalog ?? []).find((e) => e.key === prefillFrom.catalogKey);
  const wb = entry?.kind === 'data' ? entry.writeBack : undefined;
  if (!wb?.writableFields || wb.writableFields.length === 0) {
    return { kind: 'disabled', reason: '이 시스템 조회는 읽기 전용입니다' };
  }
  if (!writeBackWritableFieldMatch(prefillFrom.select, wb.writableFields)) {
    // 표시 필드(콤보 textField='display' 등)는 정본 필드로 저장되진 않지만, 카탈로그가 keyed upsert
    // writer(upsertKey 선언)면 "선택 기억"(useAsDefault)은 유효하다 — 서버는 writer 등록만 있으면
    // useAsDefault를 `__last:<bindingKey>` 저장소에 기억한다(writable 필드 매칭 불요, service.ts
    // lastUsedTargets). 완전 비활성하면 이미 켜진 기억 설정을 UI에서 끌 수 없어 트랩되므로
    // (2026-07-21 사업장 담당자 초기화 불가 제보), keyed-upsert 카탈로그면 remember-only로 열어준다.
    if (wb.upsertKey) {
      return {
        kind: 'catalog',
        catalogLabel: entry?.label ?? prefillFrom.catalogKey,
        scope: wb.scope,
        rememberOnly: true,
      };
    }
    return { kind: 'disabled', reason: '자동 계산되는 필드라 역저장할 수 없습니다' };
  }
  return {
    kind: 'catalog',
    catalogLabel: entry?.label ?? prefillFrom.catalogKey,
    scope: wb.scope,
  };
}

/** 카탈로그 writer의 선언 스코프(미선언=현장) — 반대 스코프 옵션 비활성 판정과 안내문이 공유. */
export function writeBackCatalogScope(
  avail: WriteBackAvailability,
): 'site' | 'organization' | undefined {
  return avail.kind === 'catalog' ? (avail.scope ?? 'site') : undefined;
}

/**
 * `WriteBackSelector` select 아래 안내문 — 우선순위(위가 이긴다):
 *  1. 행 스코프 미충족(`rowScope.kind==='ineligible'`): 사유. 이미 켜져 있으면 해제 권고를 덧붙인다.
 *  2. keyed upsert 페어링 커버(`pairedCoverage`): 켜져 있으면 「어긋난 별개 채널」 해제 권고, 아니면
 *     「별도 지정 불필요」.
 *  3. 제네릭 저장소.
 *  4. 카탈로그 writer: remember-only(표시값 — 선택 기억만) / 정본 저장(+ 행 스코프 충족이면
 *     `ROW_SCOPE_WRITE_BACK_ELIGIBLE_NOTE` 병기).
 *  5. 비활성 사유.
 * 문장은 은닉 감사가 통과한 문구 그대로다(spec이 글자 단위로 못박는다).
 */
export function writeBackHelpText(args: {
  avail: WriteBackAvailability;
  writeBack: { scope: 'site' | 'organization'; useAsDefault?: boolean } | undefined;
  pairedCoverage: { keyLabel: string } | null | undefined;
  rowScope: RowScopeWriteBackJudgement | undefined;
}): string {
  const { avail, writeBack, pairedCoverage, rowScope } = args;
  if (rowScope?.kind === 'ineligible') {
    return writeBack
      ? `${rowScope.reason} — 이 설정은 저장 시 아무것도 쓰지 않으니 '없음(보고서만)'으로 해제하세요`
      : rowScope.reason;
  }
  if (pairedCoverage) {
    return writeBack
      ? `'${pairedCoverage.keyLabel}'의 역전파에 이미 함께 저장되는 위젯입니다 — 별도 역전파는 어긋난 별개 채널이 되니 '없음(보고서만)'으로 해제하세요`
      : `'${pairedCoverage.keyLabel}'의 선택 전파로 연결되어 있어 그 위젯의 역전파에 함께 저장됩니다 — 별도 지정 불필요`;
  }
  if (avail.kind === 'generic') {
    return '저장 시 현장/조직 저장소에 보관되어 다음 보고서에서 자동 채움으로 재사용됩니다';
  }
  if (avail.kind === 'catalog') {
    if (avail.rememberOnly) {
      return `표시값이라 '${avail.catalogLabel}' 정본 필드로는 저장되지 않지만, 선택을 기억해 다음 보고서 기본값으로 쓸 수 있습니다(아래 체크박스). 끄려면 '없음(보고서만)'으로.`;
    }
    const scopeLabel = writeBackCatalogScope(avail) === 'organization' ? '조직' : '현장';
    return `'${avail.catalogLabel}' 정본(${scopeLabel} 스코프)에 저장됩니다${
      rowScope?.kind === 'eligible' ? ` — ${ROW_SCOPE_WRITE_BACK_ELIGIBLE_NOTE}` : ''
    }`;
  }
  return avail.reason;
}
